<template>
  <div v-redirect-if-no-permission:VIEW_FINANCE_REPORTS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <v-card>
      <div class="fin-header fin-header--sticky fr-no-print">
        <div class="fin-toolbar">
          <span class="fin-title">{{ t("title") }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="expandAll">
              <v-icon small>unfold_more</v-icon>
            </v-btn>
            <span>{{ c("expandAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="collapseAll">
              <v-icon small>unfold_less</v-icon>
            </v-btn>
            <span>{{ c("collapseAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="exportExcel">
              <v-icon small>grid_on</v-icon>
            </v-btn>
            <span>{{ c("exportExcel") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="downloadCsv">
              <v-icon small>file_download</v-icon>
            </v-btn>
            <span>{{ c("exportCsv") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="exportPrint">
              <v-icon small>print</v-icon>
            </v-btn>
            <span>{{ c("print") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="loadReport">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ c("refresh") }}</span>
          </v-tooltip>
        </div>
        <div class="fin-filters">
          <v-text-field v-model="filters.from" type="date" :label="c('from')" hide-details class="fin-field"></v-text-field>
          <v-text-field v-model="filters.to" type="date" :label="c('to')" hide-details class="fin-field"></v-text-field>
          <v-btn small color="primary" @click="loadReport">{{ c("apply") }}</v-btn>
        </div>
      </div>

      <template v-if="hasReport">
        <div v-for="(warning, index) in report.warnings" :key="'w' + index" class="fin-alert-error">
          <v-icon small color="#c62828">warning</v-icon>
          <span>{{ warning.text }}</span>
        </div>

        <div class="fin-summary">
          <div class="fin-stat">
            <span class="fin-stat-label">{{ c("period") }}</span>
            <span class="fin-stat-value">{{ frPeriodText(report.period) }}</span>
          </div>
          <v-chip small :color="report.is_balanced ? 'green' : 'red'" text-color="white">
            <v-icon small class="me-1">{{ report.is_balanced ? "check_circle" : "warning" }}</v-icon>
            {{ report.is_balanced ? c("balanced") : c("notBalanced") }}
          </v-chip>
        </div>

        <div v-for="section in report.sections" :key="section.currency" class="ec-section">
          <div class="ec-section-head">
            <span class="ec-section-title">{{ t("sectionTitle") }}: {{ section.currency_label }}</span>
            <span v-if="section.has_unposted" class="ec-unposted-summary">
              {{ t("unpostedSummary") }}: {{ formatMoney(section.unposted_profit.closing) }}
            </span>
          </div>
          <div class="ec-table-wrap fin-table-wrap">
            <table class="ec-table fin-table">
              <thead>
                <tr>
                  <th class="ec-col-label">{{ t("statement") }}</th>
                  <th v-for="column in report.columns" :key="column.key" class="fin-num-head">
                    <div>{{ columnLabel(column) }}</div>
                    <div class="ec-codes fin-num">{{ columnCodes(column) }}</div>
                  </th>
                  <th class="fin-num-head">{{ t("total") }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="row in section.rows">
                  <tr :key="section.currency + row.key" :class="rowClasses(row)">
                    <td class="ec-col-label">
                      <v-btn
                        v-if="row.entries_count"
                        icon
                        small
                        class="ec-toggle fr-no-print"
                        :title="isExpanded(section.currency, row.key) ? t('hideEntries') : t('showEntries')"
                        @click="toggle(section.currency, row.key)"
                      >
                        <v-icon small>{{ isExpanded(section.currency, row.key) ? "remove" : "add" }}</v-icon>
                      </v-btn>
                      <span v-else class="ec-toggle-spacer"></span>
                      <span>{{ rowLabel(row) }}</span>
                      <span v-if="row.entries_count" class="ec-count">({{ row.entries_count }})</span>
                    </td>
                    <td v-for="column in report.columns" :key="column.key" class="fin-num" :class="amountClass(row.values[column.key])">
                      {{ amountText(row.values[column.key], row.kind) }}
                      <div v-if="unpostedOf(row, column.key)" class="ec-unposted" :title="t('unpostedHint')">
                        {{ report.unposted_profit_label_ar || t("unposted") }}: {{ formatMoney(unpostedOf(row, column.key)) }}
                      </div>
                    </td>
                    <td class="fin-num ec-total" :class="amountClass(row.total)">{{ amountText(row.total, row.kind) }}</td>
                  </tr>
                  <template v-if="isExpanded(section.currency, row.key)">
                    <tr v-for="entry in row.entries" :key="section.currency + row.key + '-' + entry.journal_entry_id" class="ec-entry-row">
                      <td class="ec-col-label ec-entry-label">
                        <span class="ec-ref">{{ entry.reference_no || "#" + entry.journal_entry_id }}</span>
                        <span class="ec-date">{{ entry.journal_date }}</span>
                        <div v-if="entry.memo" class="ec-memo">{{ entry.memo }}</div>
                      </td>
                      <td v-for="column in report.columns" :key="column.key" class="fin-num" :class="amountClass(entry.values[column.key])">
                        {{ amountText(entry.values[column.key], "movement") }}
                      </td>
                      <td class="fin-num" :class="amountClass(entry.total)">{{ amountText(entry.total, "movement") }}</td>
                    </tr>
                  </template>
                </template>
              </tbody>
              <tfoot>
                <tr :class="section.is_balanced ? 'ec-check-ok' : 'ec-check-bad'">
                  <td class="ec-col-label">{{ t("check") }}</td>
                  <td v-for="check in section.checks" :key="check.key" class="fin-num">
                    {{ formatMoney(check.difference) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div v-if="section.outside_accounts.length" class="ec-outside">
            <div class="ec-outside-title">{{ t("outsideTitle") }}</div>
            <table class="ec-table ec-table--compact fin-table">
              <thead>
                <tr>
                  <th>{{ c("accountCode") }}</th>
                  <th>{{ c("accountName") }}</th>
                  <th class="fin-num-head">{{ t("opening") }}</th>
                  <th class="fin-num-head">{{ t("movement") }}</th>
                  <th class="fin-num-head">{{ t("closing") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="account in section.outside_accounts" :key="account.id">
                  <td class="fin-code">{{ account.code }}</td>
                  <td>{{ account.name }}</td>
                  <td class="fin-num">{{ formatMoney(account.opening) }}</td>
                  <td class="fin-num">{{ formatMoney(account.movement) }}</td>
                  <td class="fin-num">{{ formatMoney(account.closing) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="report.notes && report.notes.length" class="ec-notes">
          <div class="ec-notes-title">{{ t("notes") }}</div>
          <ul>
            <li v-for="(note, index) in report.notes" :key="'n' + index">{{ note }}</li>
          </ul>
        </div>
      </template>
      <div v-else-if="!loading" class="ec-empty">{{ c("noData") }}</div>
    </v-card>
  </div>
</template>

<script>
/*
 * Report 2.4 of the accountant's document 2026-10-03 (equity-changes):
 * statement of changes in equity. Columns = capital 311, reserves 321-324,
 * current-year profit 332, retained earnings (33 minus 332), total; rows =
 * opening, the classified movements in her order, closing. A movement row
 * expands to the journal entries behind it.
 * API: this.api.ACTIONS.ACCOUNTING_EQUITY_CHANGES (GET from, to, format=json|csv).
 * Money in the payload is in STORED units; formatMoney / the export mixin
 * divide by 100 for display.
 */
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";
import financeReportExport from "@/mixins/financeReportExport";
import { companyToday } from "@/util/companyTime";

const PREFIX = "finance.reports2.equityChanges.";

export default {
  name: "EquityChanges",
  title() {
    return this.$t("finance.reports2.equityChanges.pageTitle");
  },
  components: { FinanceSectionNav, LoadingComponent },
  mixins: [financeReportExport],
  data() {
    // Final-version doc 2026-10-05: the company (Damascus) day on the
    // server-synced clock, not the PC calendar.
    const today = companyToday();
    return {
      loading: false,
      report: null,
      expanded: {},
      filters: {
        // Default period: the current fiscal year to date.
        from: today.slice(0, 4) + "-01-01",
        to: today,
      },
    };
  },
  computed: {
    hasReport() {
      return !!(this.report && Array.isArray(this.report.sections));
    },
  },
  mounted() {
    this.loadReport();
  },
  methods: {
    t(key) {
      return this.$t(PREFIX + key);
    },
    c(key) {
      return this.$t("finance.reports2.common." + key);
    },
    params() {
      return { from: this.filters.from, to: this.filters.to };
    },
    loadReport() {
      if (!this.filters.from || !this.filters.to) {
        window.getApp.$emit("SHOW_TOAST", this.c("period"), "red");
        return;
      }
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_EQUITY_CHANGES, { params: this.params() })
        .then((response) => {
          this.report = (response.data && response.data.data) || null;
          this.expanded = {};
        })
        .catch((error) => {
          window.getApp.$emit("SHOW_TOAST", this.frErrorMessage(error, this.c("failedLoad")), "red");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    columnLabel(column) {
      const key = PREFIX + "columns." + column.key;
      return this.$te(key) ? this.$t(key) : column.label_ar;
    },
    columnCodes(column) {
      let text = "(" + (column.codes || []).join(", ") + ")";
      if (column.minus_codes && column.minus_codes.length) {
        text = "(" + (column.codes || []).join(", ") + " - " + column.minus_codes.join(", ") + ")";
      }
      return text;
    },
    rowLabel(row) {
      const key = PREFIX + "rows." + row.key;
      return this.$te(key) ? this.$t(key) : row.label_ar;
    },
    rowClasses(row) {
      return {
        "ec-row-opening": row.kind === "opening",
        "ec-row-closing": row.kind === "closing",
        "ec-row-empty": row.kind === "movement" && !Number(row.total) && !row.entries_count && !row.unposted,
      };
    },
    amountText(value, kind) {
      const n = Number(value) || 0;
      if (kind === "movement" && n === 0) {
        return "-";
      }
      const text = this.formatMoney(n);
      return kind === "movement" && n > 0 ? "+" + text : text;
    },
    amountClass(value) {
      return Number(value) < 0 ? "ec-negative" : "";
    },
    unpostedOf(row, columnKey) {
      const value = row.unposted ? Number(row.unposted[columnKey]) : 0;
      return value || 0;
    },
    expandKey(currency, rowKey) {
      return currency + "|" + rowKey;
    },
    isExpanded(currency, rowKey) {
      return !!this.expanded[this.expandKey(currency, rowKey)];
    },
    toggle(currency, rowKey) {
      const next = Object.assign({}, this.expanded);
      const key = this.expandKey(currency, rowKey);
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }
      this.expanded = next;
    },
    expandAll() {
      const next = {};
      ((this.report && this.report.sections) || []).forEach((section) => {
        section.rows.forEach((row) => {
          if (row.entries_count) {
            next[this.expandKey(section.currency, row.key)] = true;
          }
        });
      });
      this.expanded = next;
    },
    collapseAll() {
      this.expanded = {};
    },

    // ---------------------------------------------------------------
    // Exports (same rows as the screen; expanded rows carry their entries)
    // ---------------------------------------------------------------
    exportColumns() {
      const columns = [{ key: "label", label: this.t("statement"), type: "text", width: 36, indent: true }];
      this.report.columns.forEach((column) => {
        columns.push({ key: column.key, label: this.columnLabel(column) + " " + this.columnCodes(column), type: "money", width: 18 });
      });
      columns.push({ key: "total", label: this.t("total"), type: "money", width: 18 });
      columns.push({ key: "note", label: this.t("notes"), type: "text", width: 26 });
      return columns;
    },
    exportRows(section) {
      const rows = [];
      const unpostedLabel = this.report.unposted_profit_label_ar || this.t("unposted");
      section.rows.forEach((row) => {
        const out = { label: this.rowLabel(row), total: row.total, _kind: row.kind === "movement" ? "detail" : row.kind, _level: 0 };
        this.report.columns.forEach((column) => {
          out[column.key] = row.values[column.key];
        });
        const unposted = row.unposted ? Number(row.unposted.current_year_profit) || 0 : 0;
        out.note = unposted ? unpostedLabel + ": " + this.formatMoney(unposted) : "";
        rows.push(out);
        if (this.isExpanded(section.currency, row.key)) {
          row.entries.forEach((entry) => {
            const detail = {
              label: (entry.reference_no || "#" + entry.journal_entry_id) + " " + (entry.journal_date || "") + (entry.memo ? " - " + entry.memo : ""),
              total: entry.total,
              note: "",
              _kind: "detail",
              _level: 2,
            };
            this.report.columns.forEach((column) => {
              detail[column.key] = entry.values[column.key];
            });
            rows.push(detail);
          });
        }
      });
      const check = { label: this.t("check"), note: section.is_balanced ? this.c("balanced") : this.c("notBalanced"), _kind: "total", _highlight: section.is_balanced ? "ok" : "error" };
      section.checks.forEach((item) => {
        check[item.key] = item.difference;
      });
      rows.push(check);
      return rows;
    },
    outsideColumns() {
      return [
        { key: "code", label: this.c("accountCode"), type: "code" },
        { key: "name", label: this.c("accountName"), type: "text", width: 34 },
        { key: "opening", label: this.t("opening"), type: "money" },
        { key: "movement", label: this.t("movement"), type: "money" },
        { key: "closing", label: this.t("closing"), type: "money" },
      ];
    },
    exportNotes() {
      const notes = [];
      (this.report.warnings || []).forEach((warning) => notes.push(warning.text));
      (this.report.notes || []).forEach((note) => notes.push(note));
      return notes;
    },
    sectionTitle(section) {
      return this.t("sectionTitle") + ": " + section.currency_label;
    },
    exportExcel() {
      if (!this.hasReport) return;
      const sheets = [];
      const columns = this.exportColumns();
      const multi = this.report.sections.length > 1;
      this.report.sections.forEach((section) => {
        sheets.push({
          name: this.t("sheetName") + (multi ? " " + section.currency : ""),
          title: multi ? this.sectionTitle(section) : undefined,
          columns: columns,
          rows: this.exportRows(section),
          notes: this.exportNotes(),
        });
        if (section.outside_accounts.length) {
          sheets.push({
            name: this.t("outsideSheet") + (multi ? " " + section.currency : ""),
            title: this.t("outsideTitle"),
            columns: this.outsideColumns(),
            rows: section.outside_accounts,
          });
        }
      });
      this.frExportXlsx({ fileName: "equity-changes", header: this.report.header, sheets: sheets });
    },
    exportPrint() {
      if (!this.hasReport) return;
      const columns = this.exportColumns();
      const sections = [];
      const multi = this.report.sections.length > 1;
      this.report.sections.forEach((section) => {
        sections.push({ title: multi ? this.sectionTitle(section) : "", columns: columns, rows: this.exportRows(section) });
        if (section.outside_accounts.length) {
          sections.push({ title: this.t("outsideTitle"), columns: this.outsideColumns(), rows: section.outside_accounts });
        }
      });
      this.frPrint({ header: this.report.header, sections: sections, notes: this.exportNotes(), orientation: "landscape" });
    },
    downloadCsv() {
      this.frDownloadCsv(this.api.ACTIONS.ACCOUNTING_EQUITY_CHANGES, this.params(), "equity-changes");
    },
  },
};
</script>

<style scoped>
.ec-section {
  padding: 4px 8px 12px;
}
.ec-section-head {
  display: flex;
  align-items: baseline;
  gap: 16px;
  padding: 6px 4px;
}
.ec-section-title {
  font-weight: 700;
  font-size: 14px;
}
.ec-unposted-summary {
  font-size: 12px;
  color: #e65100;
}
/* Table chrome comes from the shared .fin-table layer (finance-compact.css,
   final-version doc 2026-10-05). */
.ec-table-wrap {
  min-height: 0;
  margin: 0;
}
.ec-table th,
.ec-table td {
  vertical-align: top;
}
.ec-table--compact {
  width: auto;
  min-width: 50%;
}
.ec-codes {
  font-size: 11px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
}
.ec-total {
  font-weight: 700;
}
.ec-col-label {
  min-width: 230px;
  white-space: nowrap;
}
.ec-toggle {
  margin: 0;
  margin-inline-end: 4px;
  width: 22px;
  height: 22px;
}
.ec-toggle-spacer {
  display: inline-block;
  width: 30px;
}
.ec-count {
  color: rgba(0, 0, 0, 0.5);
  font-size: 11px;
  margin-inline-start: 4px;
}
.ec-row-opening td,
.ec-row-closing td {
  background: #f5f5f5;
  font-weight: 700;
}
.ec-row-empty td {
  color: rgba(0, 0, 0, 0.45);
}
.ec-entry-row td {
  background: #fafafa;
  font-size: 12px;
}
.ec-entry-label {
  padding-inline-start: 40px !important;
  white-space: normal;
}
.ec-ref {
  font-weight: 600;
  direction: ltr;
  unicode-bidi: embed;
}
.ec-date {
  color: rgba(0, 0, 0, 0.55);
  margin-inline-start: 8px;
  direction: ltr;
  unicode-bidi: embed;
}
.ec-memo {
  color: rgba(0, 0, 0, 0.65);
}
.ec-negative {
  color: #c62828;
}
.ec-unposted {
  font-size: 11px;
  color: #e65100;
  font-weight: 600;
}
.ec-check-ok td {
  background: #e8f5e9;
  font-weight: 700;
}
.ec-check-bad td {
  background: #ffcdd2;
  font-weight: 700;
}
.ec-outside {
  margin-top: 12px;
}
.ec-outside-title {
  font-weight: 700;
  color: #b71c1c;
  padding: 4px;
}
.ec-notes {
  padding: 4px 16px 16px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.7);
}
.ec-notes-title {
  font-weight: 700;
}
.ec-empty {
  padding: 32px;
  text-align: center;
  color: rgba(0, 0, 0, 0.55);
}
@media print {
  .fr-no-print {
    display: none !important;
  }
}
</style>
